/**
 * Copyright (c) 2016, Patrick Steele-Idem
 * https://github.com/patrick-steele-idem/morphdom
 *
 * Permission to use, copy, modify, and/or distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 */

((L, D, U) => {
	const specialElHandlers = {
		OPTION(fromEl, toEl) {
			_syncBooleanAttrProp(fromEl, toEl, 'selected');
		},

		INPUT(fromEl, toEl) {
			_syncBooleanAttrProp(fromEl, toEl, 'checked');
			_syncBooleanAttrProp(fromEl, toEl, 'disabled');

			if (fromEl.value !== toEl.value) {
				fromEl.value = toEl.value;
			}

			if (! toEl.hasAttribute('value')) {
				fromEl.removeAttribute('value');
			}
		},

		TEXTAREA(fromEl, toEl) {
			const newValue = toEl.value;

			if (fromEl.value !== newValue) {
				fromEl.value = newValue;
			}

			const firstChild = fromEl.firstChild;

			if (firstChild) {
				const oldValue = firstChild.nodeValue;

				if (oldValue == newValue || (! newValue && oldValue == fromEl.placeholder)) {
					return;
				}

				firstChild.nodeValue = newValue;
			}
		},

		SELECT(fromEl, toEl) {
			if (! toEl.hasAttribute('multiple')) {
				let curChild = fromEl.firstChild;
				let i = 0;
				let nodeName;

				while (curChild) {
					nodeName = curChild.nodeName;

					if (nodeName && nodeName.toUpperCase() === 'OPTION') {
						if (curChild.hasAttribute('selected')) {
							break;
						}

						i++;
					}

					curChild = curChild.nextSibling;
				}

				fromEl.selectedIndex = i;
			}
		}
	};

	/**
	 * Synchronize boolean attribute properties
	 *
	 * @private
	 * @param {HTMLElement} node
	 * @param {String} attr
	 * @returns {Boolean}
	 */
	const _hasAttribute = (node, attr) => {
		return node.nodeType === 1 && node.getAttribute(attr);
	};

	/**
	 * Synchronize boolean attribute properties
	 *
	 * @param {HTMLElement} fromEl
	 * @param {HTMLElement} toEl
	 * @param {String} name
	 * @private
	 */
	const _syncBooleanAttrProp = (fromEl, toEl, name) => {
		if (fromEl[name] !== toEl[name]) {
			fromEl[name] = toEl[name];

			fromEl[name] ?
				fromEl.setAttribute(name, '') :
				fromEl.removeAttribute(name);
		}
	};

	/**
	 * Loop over all the attributes on the target node and make sure the
	 * original DOM node has the same attributes. If an attribute
	 * found on the original node is not on the new node then remove it from
	 * the original node
	 *
	 * @private
	 * @param {HTMLElement} fromNode
	 * @param {HTMLElement} toNode
	 */
	const _morphAttrs = (fromNode, toNode) => {
		const to = toNode.attributes;
		const from = fromNode.attributes;
		const names = new Set();

		// Update/add attributes from target
		for (let i = 0; i < to.length; i++) {
			const attr = to[i];
			const attrName = attr.name;

			names.add(attrName);

			if (fromNode.getAttribute(attrName) !== attr.value) {
				fromNode.setAttribute(attrName, attr.value);
			}
		}

		// Remove attributes not in target (reverse loop for safe removal)
		for (let i = from.length - 1; i >= 0; i--) {
			const attrName = from[i].name;

			if (! names.has(attrName)) {
				fromNode.removeAttribute(attrName);
			}
		}
	};

	/**
	 * Copies the children of one DOM element to another
	 *
	 * @private
	 * @param {HTMLElement} fromEl
	 * @param {HTMLElement} toEl
	 * @returns {HTMLElement}
	 */
	const _moveChildren = (fromEl, toEl) => {
		while (fromEl.firstChild) {
			toEl.appendChild(fromEl.firstChild);
		}

		return toEl;
	};

	L.view.diff = (fromNode, toNode, replace) => {
		// Used to store DOM elements with IDs
		const savedEls = new Map();
		const unmatchedEls = new Map();

		/**
		 * @param {HTMLElement} node
		 * @param {Boolean} [nestedInSavedEl=false]
		 */
		const removeNodeHelper = (node, nestedInSavedEl) => {
			if (node.nodeType === 1) {
				const id = node.getAttribute('data-uid');

				if (id) {
					savedEls.set(id, node);
				}

				for (const child of node.childNodes) {
					removeNodeHelper(child, nestedInSavedEl || id);
				}
			}
		};

		/**
		 * @param {HTMLElement} node
		 */
		const walkDiscardedChildNodes = node => {
			if (node.nodeType === 1) {
				for (const child of node.childNodes) {
					if (! _hasAttribute(child, 'data-uid')) {
						walkDiscardedChildNodes(child);
					}
				}
			}
		};

		/**
		 * @param {HTMLElement} node
		 * @param {HTMLElement} parentNode
		 * @param {Boolean} alreadyVisited
		 */
		const removeNode = (node, parentNode, alreadyVisited) => {
			parentNode.removeChild(node);

			if (alreadyVisited) {
				if (! _hasAttribute(node, 'data-uid')) {
					walkDiscardedChildNodes(node);
				}
			} else {
				removeNodeHelper(node);
			}
		};

		/**
		 * @param {HTMLElement|Node} fromEl
		 * @param {HTMLElement|Node} toEl
		 * @param {Boolean} alreadyVisited
		 */
		const morphEl = (fromEl, toEl, alreadyVisited) => {
			// Don't process elements specifically excluded or unchanged
			if (fromEl.hasAttribute('data-ignore') || fromEl.isEqualNode(toEl)) {
				return;
			}

			const uid = toEl.getAttribute('data-uid');

			if (uid) {
				// If an element with a UID morphed then it will be in the
				// final DOM so clear it out of the elements collection
				savedEls.delete(uid);
			}

			_morphAttrs(fromEl, toEl);

			if (fromEl.tagName !== 'TEXTAREA') {
				let curToNodeChild = toEl.firstChild;
				let curFromNodeChild = fromEl.firstChild;
				let curToNodeId;
				let fromNextSibling;
				let toNextSibling;
				let savedEl;
				let unmatchedEl;

				outer: while (curToNodeChild) {
					toNextSibling = curToNodeChild.nextSibling;
					curToNodeId = curToNodeChild.nodeType === 1 ?
						curToNodeChild.getAttribute('data-uid') : U;

					while (curFromNodeChild) {
						const curFromNodeType = curFromNodeChild.nodeType;
						const curFromNodeId = curFromNodeType === 1 ?
							curFromNodeChild.getAttribute('data-uid') : U;

						fromNextSibling = curFromNodeChild.nextSibling;

						if (! alreadyVisited) {
							if (curFromNodeId && (unmatchedEl = unmatchedEls.get(curFromNodeId))) {
								unmatchedEl.parentNode.replaceChild(
									curFromNodeChild, unmatchedEl
								);

								morphEl(curFromNodeChild, unmatchedEl, alreadyVisited);

								curFromNodeChild = fromNextSibling;

								continue;
							}
						}

						if (curFromNodeType === curToNodeChild.nodeType) {
							let isCompatible = false;

							if (curFromNodeType === 1) {
								// Both nodes being compared are Element nodes
								if (curFromNodeChild.tagName === curToNodeChild.tagName) {
									// We have compatible DOM elements
									if (curFromNodeId || curToNodeId) {
										// If either DOM element has an ID then we handle those
										// differently since we want to match up by ID
										if (curToNodeId === curFromNodeId) {
											isCompatible = true;
										}
									} else {
										isCompatible = true;
									}
								}

								if (isCompatible) {
									// We found compatible DOM elements so transform the current
									// "from" node to match the current target DOM node
									morphEl(curFromNodeChild, curToNodeChild, alreadyVisited);
								}
							} else if (curFromNodeType === 3) {
								// Both nodes being compared are Text nodes
								isCompatible = true;

								if (curFromNodeChild.nodeValue !== curToNodeChild.nodeValue) {
									curFromNodeChild.nodeValue = curToNodeChild.nodeValue;
								}
							}

							if (isCompatible) {
								curToNodeChild = toNextSibling;
								curFromNodeChild = fromNextSibling;

								continue outer;
							}
						}

						// No compatible match so remove the old node from
						// the DOM and continue trying to find a match in
						// the original DOM
						removeNode(curFromNodeChild, fromEl, alreadyVisited);

						curFromNodeChild = fromNextSibling;
					}

					if (curToNodeId) {
						if ((savedEl = savedEls.get(curToNodeId))) {
							morphEl(savedEl, curToNodeChild, true);
							curToNodeChild = savedEl;

							// We want to append the saved element instead
						} else {
							// The current DOM element in the target tree has an ID,
							// but we did not find a match in any of the corresponding
							// siblings. We just put the target element in the old DOM tree
							// but if we later find an element in the old DOM tree that has
							// a matching ID then we will replace the target element
							// with the corresponding old element and morph the old element
							unmatchedEls.set(curToNodeId, curToNodeChild);
						}
					}

					// If we got this far then we did not find a candidate match for our "to node"
					// and we exhausted all the children "from" nodes. Therefore, we will just
					// append the current "to node" to the end
					fromEl.appendChild(curToNodeChild);

					curToNodeChild = toNextSibling;
					curFromNodeChild = fromNextSibling;
				}

				// We have processed all the "to nodes". If
				// curFromNodeChild is non-null then we still have some from
				// nodes left over that need to be removed
				while (curFromNodeChild) {
					fromNextSibling = curFromNodeChild.nextSibling;

					removeNode(curFromNodeChild, fromEl, alreadyVisited);

					curFromNodeChild = fromNextSibling;
				}
			}

			const specialElHandler = specialElHandlers[fromEl.tagName];

			if (specialElHandler) {
				specialElHandler(fromEl, toEl);
			}
		};

		let morphedNode = fromNode;

		if (replace) {
			const el = fromNode.cloneNode(false);

			// Place fragment in container
			el.appendChild(toNode);

			toNode = el;
		} else {
			toNode = toNode.firstChild;
		}

		if (fromNode.tagName !== toNode.tagName) {
			morphedNode = _moveChildren(fromNode, D.createElement(toNode.tagName));
		}

		if (morphedNode !== toNode) {
			morphEl(morphedNode, toNode, false);

			savedEls.forEach(el => {
				walkDiscardedChildNodes(el);
			});
		}

		if (morphedNode !== fromNode && fromNode.parentNode) {
			// If we had to swap out the "from" node with a new node because the
			// old node was not compatible with the target node then we need to
			// replace the old DOM node in the original DOM tree. This is only
			// possible if the original DOM node was part of a DOM tree which
			// we know is the case if it has a parent node.
			fromNode.parentNode.replaceChild(morphedNode, fromNode);
		}

		return morphedNode;
	};
})(Apt, document, undefined);